import React, { useState, useEffect } from 'react';
import { 
  Send, 
  Radio, 
  Users, 
  MapPin, 
  CheckCircle2, 
  Smartphone, 
  Clock, 
  Sparkles, 
  AlertCircle,
  Eye,
  RotateCcw,
  Trash2,
  Copy,
  ExternalLink,
  Check
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { BroadcastNotification, BroadcastTarget } from '../types/admin';
import { AdminApiClient } from '../services/adminApiClient';

export const BroadcastView: React.FC = () => {
  const [campaigns, setCampaigns] = useState<BroadcastNotification[]>([
    {
      id: 'bc_001',
      title: 'Lagos Flash Thrift Drop: 20% Escrow Fee Subsidy! 🌿',
      body: 'Trade pre-owned electronics this weekend and enjoy 0% base escrow fees. Check verified deals near you.',
      deepLink: '/marketplace?filter=subsidy',
      targetAudience: 'lagos',
      recipientCount: 3840,
      status: 'sent',
      sentBy: 'Sammy Oliver (Super Admin)',
      sentAt: 'Yesterday at 04:00 PM'
    },
    {
      id: 'bc_002',
      title: 'Action Required: Submit Your NIN for Silver Tier 🛡️',
      body: 'Unlock higher transaction limits and trusted vendor badge by verifying your NIN or CAC certificate.',
      deepLink: '/kyc',
      targetAudience: 'vendors',
      recipientCount: 890,
      status: 'sent',
      sentBy: 'System Automation',
      sentAt: '3 days ago'
    }
  ]);

  // Campaign Interaction States (View, Resend, Delete)
  const [selectedCampaignForDetail, setSelectedCampaignForDetail] = useState<BroadcastNotification | null>(null);
  const [campaignToResend, setCampaignToResend] = useState<BroadcastNotification | null>(null);
  const [campaignToDelete, setCampaignToDelete] = useState<BroadcastNotification | null>(null);
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string | null>(null);

  // Composer state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('/marketplace');
  const [targetAudience, setTargetAudience] = useState<BroadcastTarget>('all');
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);

  useEffect(() => {
    AdminApiClient.getBroadcasts()
      .then((data) => {
        if (data && data.length > 0) {
          const mapped: BroadcastNotification[] = data.map((b: any) => ({
            id: b.id,
            title: b.title,
            body: b.message,
            deepLink: b.actionUrl || '/marketplace',
            targetAudience: 'all',
            recipientCount: 8420,
            status: 'sent',
            sentBy: b.sentBy || 'Super Admin',
            sentAt: new Date(b.createdAt).toLocaleDateString(),
          }));
          setCampaigns(mapped);
        }
      })
      .catch((err) => console.error('Failed to load broadcasts:', err));
  }, []);

  const getRecipientEstimate = (target: BroadcastTarget) => {
    switch (target) {
      case 'all': return 8420;
      case 'vendors': return 1250;
      case 'buyers': return 7170;
      case 'lagos': return 4900;
      case 'abuja': return 1850;
      case 'portharcourt': return 980;
      default: return 500;
    }
  };

  const handleSendBroadcast = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    try {
      await AdminApiClient.broadcastPush({
        title,
        message: body,
        type: 'PROMOTIONAL',
        actionUrl: deepLink,
      });

      const newBroadcast: BroadcastNotification = {
        id: `bc_${Date.now()}`,
        title,
        body,
        deepLink,
        targetAudience,
        recipientCount: getRecipientEstimate(targetAudience),
        status: 'sent',
        sentBy: 'Sammy Oliver (Super Admin)',
        sentAt: 'Just now'
      };

      setCampaigns([newBroadcast, ...campaigns]);
      setTitle('');
      setBody('');
      setSuccessMsg(true);
      setTimeout(() => setSuccessMsg(false), 4000);
    } catch (err) {
      console.error('Error sending broadcast push:', err);
    } finally {
      setSending(false);
    }
  };

  const handleLoadIntoComposer = (c: BroadcastNotification) => {
    setTitle(c.title);
    setBody(c.body);
    setDeepLink(c.deepLink || '/marketplace');
    setTargetAudience(c.targetAudience);
    setSelectedCampaignForDetail(null);
    setCampaignToResend(null);
    setActionSuccessMsg(`Campaign "${c.title}" loaded into composer above. You can edit and re-dispatch.`);
    setTimeout(() => setActionSuccessMsg(null), 4000);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExecuteResend = async (c: BroadcastNotification) => {
    setSending(true);
    try {
      await new Promise(r => setTimeout(r, 650));
      const resendRecord: BroadcastNotification = {
        id: `bc_${Date.now()}`,
        title: c.title,
        body: c.body,
        deepLink: c.deepLink,
        targetAudience: c.targetAudience,
        recipientCount: c.recipientCount,
        status: 'sent',
        sentBy: 'Sammy Oliver (Super Admin) [Re-Broadcast]',
        sentAt: 'Just now'
      };
      setCampaigns(prev => [resendRecord, ...prev]);
      setCampaignToResend(null);
      if (selectedCampaignForDetail?.id === c.id) {
        setSelectedCampaignForDetail(null);
      }
      setActionSuccessMsg(`Campaign "${c.title}" re-broadcasted successfully to ${c.recipientCount.toLocaleString()} devices!`);
      setTimeout(() => setActionSuccessMsg(null), 4500);
    } finally {
      setSending(false);
    }
  };

  const handleExecuteDelete = () => {
    if (!campaignToDelete) return;
    const deletedTitle = campaignToDelete.title;
    setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete.id));
    if (selectedCampaignForDetail?.id === campaignToDelete.id) {
      setSelectedCampaignForDetail(null);
    }
    setCampaignToDelete(null);
    setActionSuccessMsg(`Campaign "${deletedTitle}" record permanently removed.`);
    setTimeout(() => setActionSuccessMsg(null), 3500);
  };

  return (
    <div className="space-y-8">
      {/* Header Banner */}
      <div className="p-6 rounded-3xl border transition-colors flex flex-col md:flex-row items-start md:items-center justify-between gap-4
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="flex items-start gap-4">
          <div className="p-3.5 rounded-2xl bg-brand-500/15 border border-brand-500/30 text-brand-400 shrink-0">
            <Radio className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
                Broadcast Push Notifications & System Announcements
              </h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-brand-500/20 text-brand-300 border border-brand-500/30">
                FCM High-Priority Engine
              </span>
            </div>
            <p className="mt-1 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] max-w-2xl leading-relaxed">
              Compose and dispatch instant push notifications directly to iOS and Android devices through Firebase Cloud Messaging. Target specific audiences or geographic hubs across Nigeria.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Campaign Composer */}
        <div className="lg:col-span-7 rounded-3xl p-6 sm:p-8 border transition-colors
          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
          light:bg-white light:border-[#D5E4DC] light:shadow-sm
        ">
          <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17] mb-1 flex items-center gap-2">
            <Send className="w-4 h-4 text-brand-400" />
            <span>FCM Broadcast Composer</span>
          </h4>
          <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-6">
            Notifications are delivered instantly to active mobile notification trays and in-app feeds
          </p>

          {successMsg && (
            <div className="mb-5 p-4 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>Broadcast dispatched successfully to {getRecipientEstimate(targetAudience).toLocaleString()} device tokens!</span>
            </div>
          )}

          <div className="space-y-4 text-xs">
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Target Audience Cohort:
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {[
                  { id: 'all', label: 'All App Users (8,420)', icon: Users },
                  { id: 'vendors', label: 'Vendors Only (1,250)', icon: Sparkles },
                  { id: 'buyers', label: 'Buyers Only (7,170)', icon: Users },
                  { id: 'lagos', label: 'Lagos State (4,900)', icon: MapPin },
                  { id: 'abuja', label: 'Abuja FCT (1,850)', icon: MapPin },
                  { id: 'portharcourt', label: 'Port Harcourt (980)', icon: MapPin },
                ].map((cohort) => {
                  const Icon = cohort.icon;
                  return (
                    <button
                      key={cohort.id}
                      type="button"
                      onClick={() => setTargetAudience(cohort.id as BroadcastTarget)}
                      className={`p-3 rounded-2xl border text-left transition-all ${targetAudience === cohort.id ? 'border-brand-500 bg-brand-500/15 text-brand-400 font-bold' : 'border-[#22493c] text-[#90cbb7] hover:border-brand-500/30'}`}
                    >
                      <Icon className="w-3.5 h-3.5 mb-1 text-brand-400" />
                      <span className="block text-[11px] truncate">{cohort.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Notification Headline (Title):
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Flash 24-hr Thrift Deal in Ikeja! ⚡"
                className="w-full p-3.5 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Message Body:
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write compelling announcement text (max 180 chars recommended)..."
                rows={3}
                className="w-full p-3.5 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                In-App Deep Link Route:
              </label>
              <select
                value={deepLink}
                onChange={(e) => setDeepLink(e.target.value)}
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
              >
                <option value="/marketplace">/marketplace (Open Marketplace Feed)</option>
                <option value="/orders">/orders (Open Orders & Escrow Stepper)</option>
                <option value="/wallet">/wallet (Open Wallet Deposit/Withdrawal)</option>
                <option value="/kyc">/kyc (Open KYC Verification Screen)</option>
              </select>
            </div>

            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex items-center justify-between">
              <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Estimated reach: <strong className="text-brand-400">{getRecipientEstimate(targetAudience).toLocaleString()} devices</strong>
              </span>

              <button
                type="button"
                onClick={handleSendBroadcast}
                disabled={!title.trim() || !body.trim() || sending}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm disabled:opacity-50 transition-all"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{sending ? 'Broadcasting...' : 'Dispatch Push Campaign'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Live Smartphone Push Preview Card */}
        <div className="lg:col-span-5 rounded-3xl p-6 sm:p-8 border transition-colors flex flex-col items-center justify-center
          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
          light:bg-white light:border-[#D5E4DC] light:shadow-sm
        ">
          <h4 className="text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider mb-6">
            Live Device Notification Simulation
          </h4>

          <div className="w-full max-w-sm rounded-3xl p-4 bg-[#10231d] border border-[#22493c] shadow-xl space-y-3">
            <div className="flex items-center justify-between text-[11px] text-[#90cbb7]">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full bg-brand-500 flex items-center justify-center text-[9px] font-bold text-forest-void">
                  R
                </div>
                <span className="font-bold text-white">ReUseIt</span>
                <span>• Now</span>
              </div>
              <span>Lagos (11:00 AM)</span>
            </div>

            <div className="space-y-1">
              <p className="text-xs font-bold text-white">
                {title || 'Flash Notification Title Placeholder'}
              </p>
              <p className="text-xs text-[#90cbb7] leading-relaxed">
                {body || 'Notification message description preview showing how it renders in the user device tray...'}
              </p>
            </div>

            <div className="pt-2 border-t border-[#22493c] flex justify-between items-center text-[10px] text-brand-400 font-semibold">
              <span>Deep link: {deepLink}</span>
              <span>Tap to Open →</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Feedback Banner */}
      {actionSuccessMsg && (
        <div className="p-4 rounded-2xl bg-brand-500/15 border border-brand-500/30 text-brand-300 text-xs flex items-center justify-between gap-3 shadow-glow-sm">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-brand-400 shrink-0" />
            <span>{actionSuccessMsg}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionSuccessMsg(null)}
            className="text-xs text-[#90cbb7] hover:text-white"
          >
            ✕
          </button>
        </div>
      )}

      {/* Broadcast Delivery History Table */}
      <div className="rounded-3xl border overflow-hidden transition-colors
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="p-6 border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] flex items-center justify-between">
          <div>
            <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
              Campaign Delivery History
            </h4>
            <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-0.5">
              Review sent pushes with live smartphone simulations, re-broadcast 1-click, or purge records
            </p>
          </div>
          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-brand-500/15 text-brand-300 border border-brand-500/30">
            {campaigns.length} Recorded Campaigns
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                <th className="py-4 px-6">Campaign Title & Body</th>
                <th className="py-4 px-6">Target Cohort</th>
                <th className="py-4 px-6">Recipients Delivered</th>
                <th className="py-4 px-6">Author</th>
                <th className="py-4 px-6">Dispatched At</th>
                <th className="py-4 px-6">Delivery Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
              {campaigns.map((c) => (
                <tr key={c.id} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                  <td className="py-4 px-6 max-w-sm">
                    <div className="font-bold text-white dark:text-white light:text-[#0D1C17]">{c.title}</div>
                    <div className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] truncate">{c.body}</div>
                  </td>
                  <td className="py-4 px-6 font-semibold uppercase text-brand-400">
                    {c.targetAudience}
                  </td>
                  <td className="py-4 px-6 font-bold text-white dark:text-white light:text-[#0D1C17]">
                    {c.recipientCount.toLocaleString()} devices
                  </td>
                  <td className="py-4 px-6 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    {c.sentBy}
                  </td>
                  <td className="py-4 px-6 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    {c.sentAt}
                  </td>
                  <td className="py-4 px-6">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => setSelectedCampaignForDetail(c)}
                        title="View Full Campaign & Push Tray Simulation"
                        className="p-1.5 rounded-xl border border-[#22493c] hover:border-brand-500/40 text-[#90cbb7] hover:text-brand-400 hover:bg-brand-500/10 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCampaignToResend(c)}
                        title="Resend or Clone into Composer"
                        className="p-1.5 rounded-xl border border-[#22493c] hover:border-amber-500/40 text-[#90cbb7] hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCampaignToDelete(c)}
                        title="Delete Campaign Record"
                        className="p-1.5 rounded-xl border border-[#22493c] hover:border-rose-500/40 text-[#90cbb7] hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CAMPAIGN DETAILS & PREVIEW MODAL */}
      {selectedCampaignForDetail && (
        <Modal
          isOpen={Boolean(selectedCampaignForDetail)}
          onClose={() => setSelectedCampaignForDetail(null)}
          title={`Campaign Details: ${selectedCampaignForDetail.title}`}
          subtitle={`ID: ${selectedCampaignForDetail.id} • Dispatched ${selectedCampaignForDetail.sentAt}`}
          maxWidth="lg"
        >
          <div className="space-y-6 text-xs">
            {/* Meta Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-2xl bg-[#10231d] border border-[#22493c]">
                <span className="text-[10px] text-[#90cbb7] block">Target Cohort</span>
                <span className="font-bold text-brand-400 uppercase text-xs">{selectedCampaignForDetail.targetAudience}</span>
              </div>
              <div className="p-3 rounded-2xl bg-[#10231d] border border-[#22493c]">
                <span className="text-[10px] text-[#90cbb7] block">Delivered Devices</span>
                <span className="font-bold text-white text-xs">{selectedCampaignForDetail.recipientCount.toLocaleString()}</span>
              </div>
              <div className="p-3 rounded-2xl bg-[#10231d] border border-[#22493c]">
                <span className="text-[10px] text-[#90cbb7] block">Delivery Status</span>
                <span className="font-bold text-emerald-400 text-xs">Delivered 100%</span>
              </div>
              <div className="p-3 rounded-2xl bg-[#10231d] border border-[#22493c]">
                <span className="text-[10px] text-[#90cbb7] block">Author</span>
                <span className="font-bold text-[#90cbb7] truncate block text-xs">{selectedCampaignForDetail.sentBy}</span>
              </div>
            </div>

            {/* In-app deep link route */}
            <div className="p-3.5 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[#90cbb7]">Target Deep Link:</span>
                <code className="font-mono text-brand-400 font-bold">{selectedCampaignForDetail.deepLink || '/marketplace'}</code>
              </div>
              <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-brand-500/15 text-brand-300 font-semibold border border-brand-500/30">
                FCM Route
              </span>
            </div>

            {/* Smartphone Push Tray Render */}
            <div className="space-y-2">
              <span className="font-bold text-[#90cbb7] text-xs">Mobile Push Tray Notification Simulation:</span>
              <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-2">
                <div className="flex items-center justify-between text-[11px] text-[#90cbb7]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full bg-brand-500 flex items-center justify-center text-[9px] font-bold text-forest-void">
                      R
                    </div>
                    <span className="font-bold text-white">ReUseIt Nigeria</span>
                    <span>• {selectedCampaignForDetail.sentAt}</span>
                  </div>
                </div>
                <p className="font-bold text-white text-xs">{selectedCampaignForDetail.title}</p>
                <p className="text-[#90cbb7] text-xs leading-relaxed">{selectedCampaignForDetail.body}</p>
              </div>
            </div>

            {/* Action Bar */}
            <div className="pt-3 border-t border-[#22493c] flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => {
                  setCampaignToDelete(selectedCampaignForDetail);
                }}
                className="px-4 py-2.5 rounded-xl border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-xs font-bold transition-all flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Record</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleLoadIntoComposer(selectedCampaignForDetail)}
                  className="px-4 py-2.5 rounded-xl border border-[#22493c] hover:border-brand-500/40 text-[#90cbb7] hover:text-white text-xs font-bold transition-all flex items-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" />
                  <span>Load into Composer</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleExecuteResend(selectedCampaignForDetail);
                  }}
                  disabled={sending}
                  className="px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void text-xs font-extrabold shadow-glow-sm transition-all flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>{sending ? 'Broadcasting...' : 'Re-Broadcast Now'}</span>
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* RESEND CAMPAIGN MODAL */}
      {campaignToResend && (
        <Modal
          isOpen={Boolean(campaignToResend)}
          onClose={() => setCampaignToResend(null)}
          title={`Re-Broadcast Push Campaign?`}
          subtitle={`Target cohort: ${campaignToResend.targetAudience.toUpperCase()} (${campaignToResend.recipientCount.toLocaleString()} devices)`}
          maxWidth="md"
        >
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-2">
              <p className="font-bold text-white">{campaignToResend.title}</p>
              <p className="text-[#90cbb7] text-xs leading-relaxed">{campaignToResend.body}</p>
              <div className="pt-2 border-t border-[#22493c] flex items-center justify-between text-[10px] text-brand-400 font-semibold">
                <span>Route: {campaignToResend.deepLink || '/marketplace'}</span>
                <span>Reach: {campaignToResend.recipientCount.toLocaleString()} devices</span>
              </div>
            </div>

            <p className="text-[#90cbb7] leading-relaxed">
              Would you like to broadcast this notification immediately, or load it into the composer to edit the headline, message body, or target audience first?
            </p>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setCampaignToResend(null)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-[#22493c] text-xs font-semibold text-[#90cbb7] hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleLoadIntoComposer(campaignToResend)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-brand-500/40 bg-brand-500/10 hover:bg-brand-500/20 text-brand-300 text-xs font-bold transition-all flex items-center justify-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>Load into Composer</span>
              </button>
              <button
                type="button"
                onClick={() => handleExecuteResend(campaignToResend)}
                disabled={sending}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void text-xs font-extrabold shadow-glow-sm transition-all flex items-center justify-center gap-1.5"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>{sending ? 'Sending...' : 'Broadcast Now'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* DELETE CAMPAIGN CONFIRMATION DIALOG */}
      {campaignToDelete && (
        <ConfirmDialog
          isOpen={Boolean(campaignToDelete)}
          onClose={() => setCampaignToDelete(null)}
          onConfirm={handleExecuteDelete}
          title={`Delete Campaign Record: "${campaignToDelete.title}"?`}
          description={`Are you sure you want to delete this broadcast delivery log? This record will be purged from the campaign history table.`}
          confirmButtonText="Delete Campaign"
          variant="danger"
        />
      )}
    </div>
  );
};
